import { useAtomValue, useSetAtom } from 'jotai';
import { assemblyTemplate, assemblyFields, selectedTemplateField } from '../state';
import { useState, useMemo, useCallback } from 'react';
import {
  Box,
  Stack,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Card,
  CardContent,
  Divider,
  Alert,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import FunctionsIcon from '@mui/icons-material/Functions';
import RuleIcon from '@mui/icons-material/Rule';
import CalculateIcon from '@mui/icons-material/Calculate';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { abyssColors } from '@/theme/colors';
import { AssemblyTemplateField } from '@/__generated__/graphql';
import { PageLoader } from '../../shared/PageLoader';
import { FieldLogicConfigurator } from './FieldLogicConfigurator';
import { AbyssIcon } from '@/assets/icons/AbyssIcon';
import { allPOITypes } from '../state';

export const TemplateEngineKit = ({ structureId }: { structureId: string }) => {
  const allPointOfInterestTemplates = useAtomValue(allPOITypes);
  const selectedAssemblyTemplate = useAtomValue(assemblyTemplate);
  const fields = useAtomValue(assemblyFields);
  const setSelectedField = useSetAtom(selectedTemplateField);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFieldForConfig, setSelectedFieldForConfig] =
    useState<AssemblyTemplateField | null>(null);

  // Helper to get POI field name by key
  const getPOIFieldName = useCallback(
    (fieldKey: string): string | null => {
      if (!allPointOfInterestTemplates || allPointOfInterestTemplates.length === 0) {
        return null;
      }

      // Search through all POI templates to find a field with matching key
      for (const poiTemplate of allPointOfInterestTemplates) {
        const poiField = poiTemplate.fields?.find((f) => f.key === fieldKey);
        if (poiField?.name) {
          return poiField.name;
        }
      }

      return null;
    },
    [allPointOfInterestTemplates]
  );

  // Enrich fields with POI field names
  const enrichedFields = useMemo(() => {
    if (!fields) return [];

    return fields.map((field) => {
      // Check if this field references POI data by checking if the key exists in POI templates
      if (field.key) {
        const poiFieldName = getPOIFieldName(field.key);
        // If we found a matching POI field name and the current name is empty/missing, use the POI name
        if (poiFieldName && (!field.name || field.name.trim() === '')) {
          return {
            ...field,
            name: poiFieldName,
          };
        }
      }
      return field;
    });
  }, [fields, getPOIFieldName]);

  // Filter fields that can have logic/calculation
  const configurableFields = useMemo(() => {
    if (!enrichedFields) return [];
    return enrichedFields.filter(
      (field) =>
        field.shouldEvaluateLogic ||
        field.shouldEvaluateMath ||
        field.shouldEvaluateLogicPostCalculation ||
        field.logicRule ||
        field.calculatedBy
    );
  }, [enrichedFields]);

  // Search functionality
  const filteredFields = useMemo(() => {
    if (!enrichedFields) return [];
    const q = searchTerm.trim().toLowerCase();
    return enrichedFields.filter((field) =>
      q ? (field.name ?? '').toLowerCase().includes(q) : true
    );
  }, [enrichedFields, searchTerm]);

  const getFieldTypeIcon = (field: AssemblyTemplateField) => {
    if (field.shouldEvaluateMath || field.calculatedBy) {
      return <CalculateIcon sx={{ color: abyssColors.tertiary[500] }} />;
    }
    if (field.shouldEvaluateLogic || field.logicRule) {
      return <RuleIcon sx={{ color: abyssColors.success[500] }} />;
    }
    if (field.shouldEvaluateLogicPostCalculation) {
      return <FunctionsIcon sx={{ color: abyssColors.warning[500] }} />;
    }
    return null;
  };

  const getFieldTypeBadge = (field: AssemblyTemplateField) => {
    const badges = [];
    if (field.shouldEvaluateMath || field.calculatedBy) {
      badges.push(
        <Chip
          key="math"
          label="Calculation"
          size="small"
          sx={{
            backgroundColor: abyssColors.tertiary[50],
            color: abyssColors.tertiary[700],
            fontSize: '0.875rem',
            height: '20px',
          }}
        />
      );
    }
    if (field.shouldEvaluateLogic || field.logicRule) {
      badges.push(
        <Chip
          key="logic"
          label="Logic"
          size="small"
          sx={{
            backgroundColor: abyssColors.success[50],
            color: abyssColors.success[700],
            fontSize: '0.875rem',
            height: '20px',
          }}
        />
      );
    }
    if (field.shouldEvaluateLogicPostCalculation) {
      badges.push(
        <Chip
          key="post-calc"
          label="Post-Calc Logic"
          size="small"
          sx={{
            backgroundColor: abyssColors.warning[50],
            color: abyssColors.warning[700],
            fontSize: '0.875rem',
            height: '20px',
          }}
        />
      );
    }
    return badges;
  };

  const handleFieldClick = useCallback(
    (field: AssemblyTemplateField) => {
      setSelectedFieldForConfig(field);
      setSelectedField(field);
    },
    [setSelectedField]
  );

  if (!selectedAssemblyTemplate || !fields) {
    return <PageLoader />;
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 2 }}>
      {/* Header Section */}
      <Stack spacing={2} sx={{ mb: 3 }}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <FunctionsIcon sx={{ fontSize: '2rem', color: abyssColors.primary[500] }} />
          <Typography variant="h5" fontWeight={600} color={abyssColors.primary[700]}>
            Template Engine Configuration
          </Typography>
          <AbyssIcon iconName="beta" />
        </Stack>

        <Alert severity="info" icon={<InfoOutlinedIcon />}>
          Configure logic rules and calculations for template fields. Fields with evaluation flags
          can use conditional logic, mathematical expressions, and equations.
        </Alert>

        {/* Search Bar */}
        <TextField
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search fields by name..."
          variant="outlined"
          size="small"
          fullWidth
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="Clear search"
                    onClick={() => setSearchTerm('')}
                    edge="end"
                    size="small"
                  >
                    <CloseIcon />
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
        />

        {/* Summary Stats */}
        <Stack direction="row" spacing={2}>
          <Chip
            label={`Total Fields: ${fields.length}`}
            sx={{ backgroundColor: abyssColors.neutral[100] }}
          />
          <Chip
            label={`Configured: ${configurableFields.length}`}
            sx={{ backgroundColor: abyssColors.primary[50], color: abyssColors.primary[700] }}
          />
        </Stack>
      </Stack>

      <Divider sx={{ mb: 3 }} />

      {/* Main Content */}
      <Stack direction="row" spacing={3} sx={{ flex: 1, overflow: 'hidden' }}>
        {/* Fields List */}
        <Box
          sx={{
            width: '350px',
            overflowY: 'auto',
            pr: 2,
            borderRight: `1px solid ${abyssColors.neutral[200]}`,
          }}
        >
          <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
            Template Fields ({filteredFields.length})
          </Typography>

          <Stack spacing={1}>
            {filteredFields.map((field) => {
              const isConfigured =
                field.shouldEvaluateLogic ||
                field.shouldEvaluateMath ||
                field.shouldEvaluateLogicPostCalculation ||
                field.logicRule ||
                field.calculatedBy;
              const isSelected = selectedFieldForConfig?.id === field.id;

              return (
                <Card
                  key={field.id}
                  onClick={() => handleFieldClick(field)}
                  elevation={0}
                  sx={{
                    cursor: 'pointer',
                    border: isSelected
                      ? `2px solid ${abyssColors.primary[500]}`
                      : `1px solid ${abyssColors.neutral[200]}`,
                    backgroundColor: isSelected
                      ? abyssColors.primary[50]
                      : isConfigured
                      ? abyssColors.neutral[50]
                      : 'white',
                    transition: 'all 0.2s',
                    '&:hover': {
                      backgroundColor: isSelected
                        ? abyssColors.primary[100]
                        : abyssColors.neutral[100],
                      borderColor: abyssColors.primary[300],
                    },
                  }}
                >
                  <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Stack spacing={1}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        {getFieldTypeIcon(field)}
                        <Typography
                          variant="body2"
                          fontWeight={isSelected ? 600 : 500}
                          sx={{ flex: 1 }}
                        >
                          {field.name}
                        </Typography>
                      </Stack>

                      {isConfigured && (
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
                          {getFieldTypeBadge(field)}
                        </Stack>
                      )}

                      {field.key && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontFamily: 'monospace' }}
                        >
                          {field.key}
                        </Typography>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              );
            })}
          </Stack>
        </Box>

        {/* Configuration Panel */}
        <Box sx={{ flex: 1, overflowY: 'auto' }}>
          {selectedFieldForConfig ? (
            <FieldLogicConfigurator
              field={selectedFieldForConfig}
              template={selectedAssemblyTemplate}
              templateType="assembly"
              structureId={structureId}
              onUpdate={(updatedField) => {
                setSelectedFieldForConfig(updatedField as AssemblyTemplateField);
                setSelectedField(updatedField as AssemblyTemplateField);
              }}
            />
          ) : (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'center',
                height: '100%',
                color: abyssColors.neutral[400],
              }}
            >
              <Stack alignItems="center" spacing={2}>
                <FunctionsIcon sx={{ fontSize: '4rem' }} />
                <Typography variant="h6">Select a field to configure</Typography>
                <Typography variant="body2" color="text.secondary">
                  Choose a field from the list to view and edit its logic configuration
                </Typography>
              </Stack>
            </Box>
          )}
        </Box>
      </Stack>
    </Box>
  );
};
